import { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import CoproModal from '../settings/CoproModal';

interface Copro {
  id: string;
  numero: string;
  residence: string;
  created_at: string;
}

type CoproType = 'lefeuvre' | 'newco' | 'syndic';

interface CoproManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: CoproType;
  onSuccess?: () => void;
}

export default function CoproManagementModal({ isOpen, onClose, type = 'lefeuvre', onSuccess }: CoproManagementModalProps) {
  const [copros, setCopros] = useState<Copro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCopro, setSelectedCopro] = useState<Copro | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const getConfig = () => {
    switch (type) {
      case 'lefeuvre':
        return { tableName: 'copros', title: 'Copropriétés Lefeuvre' };
      case 'newco':
        return { tableName: 'copros_newco', title: 'Copropriétés NewCo' };
      case 'syndic':
        return { tableName: 'copros_syndic', title: 'Copropriétés Kermarrec Syndic' };
    }
  };

  const config = getConfig();

  useEffect(() => {
    if (isOpen) {
      loadCopros();
    }
  }, [isOpen, type]);

  const loadCopros = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from(config.tableName)
        .select('*')
        .order('numero');

      if (error) throw error;
      setCopros(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from(config.tableName)
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      loadCopros();
      setDeleteConfirm(null);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>

          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Gestion des {config.title}
              </h2>
              <button
                onClick={() => {
                  setSelectedCopro(null);
                  setIsModalOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={18} />
                <span>Nouvelle copropriété</span>
              </button>
            </div>

            {error && (
              <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Numéro
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Résidence
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {copros.map((copro) => (
                      <tr key={copro.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {copro.numero}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {copro.residence}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => {
                                setSelectedCopro(copro);
                                setIsModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Modifier"
                            >
                              <Edit2 size={18} />
                            </button>
                            {deleteConfirm === copro.id ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleDelete(copro.id)}
                                  className="text-red-600 hover:text-red-900 text-xs font-medium"
                                >
                                  Confirmer
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="text-gray-600 hover:text-gray-900 text-xs font-medium"
                                >
                                  Annuler
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(copro.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Supprimer"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {copros.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Aucune copropriété enregistrée
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CoproModal
        copro={selectedCopro}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCopro(null);
        }}
        onSuccess={() => {
          loadCopros();
          setIsModalOpen(false);
          setSelectedCopro(null);
          onSuccess?.();
        }}
        tableName={config.tableName}
      />
    </>
  );
}